import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Hook that auto-cycles through slide indices on a timer.
 *
 * - `goToSlide` jumps to a specific index and restarts the timer from zero.
 * - `setPaused` pauses/resumes auto-advance; elapsed time is tracked so that
 *   unpausing resumes with only the remaining time for the current slide.
 */
export default function useCarouselAutoPlay(
  count: number,
  intervalMs: number,
  options: { enabled?: boolean } = {},
) {
  const {
    enabled = true,
  } = options
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const restartKeyRef = useRef(0)
  const [restartKey, setRestartKey] = useState(0)
  const elapsedRef = useRef(0)
  const resetOnCleanupRef = useRef(false)

  // Reset activeIndex when the slide count shrinks below it
  useEffect(() => {
    setActiveIndex(prev => (prev >= count ? 0 : prev))
  }, [count])

  useEffect(() => {
    // Clear the flag so it doesn't leak across effect lifecycles.
    // When goToSlide triggers a non-paused cleanup, the cleanup reads it
    // before this line runs. When goToSlide fires while paused (no cleanup),
    // elapsed is already reset directly by goToSlide itself.
    resetOnCleanupRef.current = false

    if (!enabled || paused || count <= 1) return

    const remaining = Math.max(0, intervalMs - elapsedRef.current)
    const startTime = Date.now()
    let fired = false

    const id = window.setTimeout(() => {
      fired = true
      elapsedRef.current = 0
      setActiveIndex(prev => (prev + 1) % count)
    }, remaining)

    return () => {
      window.clearTimeout(id)
      if (!fired) {
        if (resetOnCleanupRef.current) {
          // goToSlide was called; reset elapsed rather than accumulating
          resetOnCleanupRef.current = false
          elapsedRef.current = 0
        } else {
          // Interrupted (e.g. paused); accumulate time spent so far
          elapsedRef.current += Date.now() - startTime
        }
      }
    }
  }, [enabled, count, intervalMs, paused, activeIndex, restartKey])

  const goToSlide = useCallback((index: number) => {
    setActiveIndex(prev => {
      if (prev === index) return prev

      // Reset elapsed directly so it takes effect even when paused
      // (the paused branch returns early with no cleanup to do the reset)
      resetOnCleanupRef.current = true
      elapsedRef.current = 0
      restartKeyRef.current += 1
      setRestartKey(restartKeyRef.current)

      return index
    })
  }, [])

  return { activeIndex, goToSlide, paused, setPaused }
}
